// @mui material components
import {
  Snackbar,
  Alert,
  Grid,
  Card,
  CircularProgress,
  Backdrop,
  Icon,
  Dialog,
  DialogTitle,
  DialogActions,
  DialogContent,
  DialogContentText,
  Divider,
} from "@mui/material";

import { useAuthUser } from "react-auth-kit";

// Material Dashboard 2 React components
import MDBox from "theme/components/MDBox";
import MDTypography from "theme/components/MDTypography";
// import MDBadge from "theme/components/MDBadge";
import MDInput from "theme/components/MDInput";

// Material Dashboard 2 React example components
import DashboardLayout from "app/LayoutContainers/DashboardLayout";
import DashboardNavbar from "app/Navbars/DashboardNavbar";
import Footer from "theme/Footer";

import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import axios from "axios.js";
import MDButton from "theme/components/MDButton";
import ApiConfig from "apiConfig";

import { useMaterialUIController } from "context";
import usePageTitle from "app/pageTitle";
import CompanyNotification from "app/company_notification";

// Images
// import netellerLogo from "assets/images/logos/ewallets/neteller.png";
// import paypalLogo from "assets/images/logos/ewallets/paypal.png";
// import skrillLogo from "assets/images/logos/ewallets/skrill.png";
// import zelleLogo from "assets/images/logos/ewallets/zelle.png";

function DwOffers() {
  const pageTitle = "Digital Wallet Marketplace";
  usePageTitle(pageTitle);

  const auth = useAuthUser();
  const user = auth();

  const [controller] = useMaterialUIController();
  const { darkMode } = controller;

  const navigate = useNavigate();
  const [refreshPage, setRefreshPage] = useState(false);

  const [showLoading, setShowLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [errorMsg, setErrMsg] = useState("");
  const [severity, setSeverity] = useState("");
  const handleClose = () => {
    setOpen(false);
    setErrMsg("");
  };

  const [offers, setOffers] = useState([]);
  const [destinationDetails, setDestinationDetails] = useState({});
  const [commissionEst, setCommissionEst] = useState(0.0);

  const [showCompanyNotification, setShowCompanyNotification] = useState(false);
  const [companyNotification, setCompanyNotification] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      setShowLoading(true);
      await axios
        .get("ewallets/offers.json")
        .then((result) => {
          // console.log(result);
          setShowLoading(false);
          if (result.data.status === "success") {
            setOffers(result.data.data.offers);

            if (result.data.notification) {
              setCompanyNotification(result.data.notification);
              if (result.data.notification.pages.includes("ALL")) {
                setShowCompanyNotification(true);
              } else if (result.data.notification.pages.includes("dw-offers")) {
                setShowCompanyNotification(true);
              }
            }
          } else {
            setSeverity("error");
            setErrMsg(result.data.data.message);
            setOpen(true);
          }
        })
        .catch(() => {
          setShowLoading(false);
          setSeverity("error");
          setErrMsg("An error occurred. Make sure you have an active internet connection.");
          setOpen(true);
        });
    };

    fetchData();
  }, [refreshPage]);

  function formatAmount(amt, symbol) {
    if (amt >= 1) {
      return `${symbol}${amt.toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;
    }
    return `${symbol}${amt}`;
  }

  const [currentOffer, setCurrentOffer] = useState(null);
  const [currentAmount, setCurrentAmount] = useState(0);
  const [openDialog, setOpenDialog] = useState(false);

  const handleOpenDialog = (offer) => {
    setCurrentOffer(offer);
    const minAmt = offer.min_transaction_amount / offer.currency.digital.multiplier;
    setCurrentAmount(minAmt);

    setCommissionEst(minAmt * 0.01);
    setOpenDialog(true);
  };

  const handleDialogClose = () => {
    setCurrentOffer(null);
    setCurrentAmount(0);
    setOpenDialog(false);
  };

  async function swap(reference, fundAmount, walletDestination) {
    setShowLoading(true);
    const json = {
      order_ref: `${reference}`,
      amount: fundAmount * currentOffer.currency.digital.multiplier,
      destination: walletDestination,
    };
    await axios
      .post("ewallets/offers/swap.json", json)
      .then((result) => {
        setShowLoading(false);
        if (result.data.status === "success") {
          setRefreshPage(!refreshPage);
          navigate(`/p2p/ewallets/transactions/${result.data.data.swap.reference}`);
        } else {
          setSeverity("error");
          setErrMsg(result.data.data.message);
          setOpen(true);
        }
      })
      .catch(() => {
        setShowLoading(false);
        setSeverity("error");
        setErrMsg("An error occurred. Please try again later.");
        setOpen(true);
      });
  }

  function handleSwapSubmit(event) {
    event.preventDefault();
    if (currentOffer === null) {
      setSeverity("error");
      setErrMsg("Select a P2P order.");
      setOpen(true);
      return;
    }

    if (currentAmount <= 0) {
      setSeverity("error");
      setErrMsg("Enter a valid amount.");
      setOpen(true);
      return;
    }

    if (
      currentAmount <
      currentOffer.min_transaction_amount / currentOffer.currency.digital.multiplier
    ) {
      setSeverity("error");
      setErrMsg("Amount cannot be less than the minimum amount");
      setOpen(true);
      return;
    }

    if (currentAmount > currentOffer.liquidity / currentOffer.currency.digital.multiplier) {
      setSeverity("error");
      setErrMsg("Amount cannot be more than the available amount");
      setOpen(true);
      return;
    }

    if (currentOffer.side === "SELL") {
      if (destinationDetails === null || destinationDetails.length < 1) {
        setSeverity("error");
        setErrMsg(`Please provide your ${currentOffer.digital_wallet.title} details.`);
        setOpen(true);
        return;
      }

      const passed = currentOffer.digital_wallet.parameters.every((param) => {
        if (!(param.key in destinationDetails)) {
          setSeverity("error");
          setErrMsg(`Your ${currentOffer.digital_wallet.title} ${param.display_name} is required`);
          setOpen(true);
          return false;
        }

        return true;
      });

      if (!passed) {
        return;
      }
    }

    swap(currentOffer.reference, currentAmount, destinationDetails);
    handleDialogClose();
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={showLoading}
        onClick={handleClose}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <Snackbar
        open={open}
        autoHideDuration={6000}
        onClose={handleClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert onClose={handleClose} severity={severity} sx={{ width: "100%" }}>
          {errorMsg}
        </Alert>
      </Snackbar>
      {companyNotification !== null && showCompanyNotification ? (
        <CompanyNotification
          title={companyNotification.title}
          description={companyNotification.description}
        />
      ) : (
        <> </>
      )}
      {currentOffer !== null ? (
        <Dialog open={openDialog} onClose={handleDialogClose} fullWidth maxWidth="xs">
          <form onSubmit={handleSwapSubmit}>
            <DialogTitle>
              <MDBox>{currentOffer.side === "SELL" ? "Buy" : "Sell"} Swap</MDBox>
            </DialogTitle>
            <DialogContent>
              <DialogContentText>
                <MDBox>
                  How much {currentOffer.currency.digital.code}
                  {" would you like to "}
                  {currentOffer.side === "BUY" ? "sell to" : "buy from"} this person?
                </MDBox>
              </DialogContentText>
              <MDBox mt={3}>
                <MDInput
                  value={currentAmount}
                  name="amount"
                  margin="dense"
                  label={`${currentOffer.currency.digital.code} Amount`}
                  type="decimal"
                  fullWidth
                  variant="outlined"
                  onChange={(e) => {
                    setCurrentAmount(e.target.value);
                    const com = e.target.value * 0.01;
                    setCommissionEst(com);
                  }}
                />
              </MDBox>
              {currentOffer ? (
                <MDBox mt={1} display="flex" justifyContent="flex-start">
                  <Alert severity="success">
                    <MDTypography variant="h6" fontWeight="light">
                      You earn:&nbsp;
                      <b>{formatAmount(commissionEst, currentOffer.currency.digital.symbol)}</b>
                    </MDTypography>
                  </Alert>
                </MDBox>
              ) : (
                <> </>
              )}

              {currentOffer.side === "SELL" ? (
                <MDBox mt={5}>
                  <MDTypography variant="h6">
                    <small>
                      Enter your {currentOffer.digital_wallet.title} details where funds will be
                      delivered to.
                    </small>
                  </MDTypography>
                  {currentOffer.digital_wallet.parameters.map((param) => (
                    <MDBox mt={2}>
                      <MDInput
                        name={param.key}
                        label={param.display_name}
                        sx={{ minWidth: 120, width: "100%" }}
                        // value={destinationDetails[param.key] || ""}
                        onChange={(e) => {
                          destinationDetails[param.key] = e.target.value;
                          setDestinationDetails(destinationDetails);
                        }}
                      />
                    </MDBox>
                  ))}
                </MDBox>
              ) : (
                <> </>
              )}
            </DialogContent>
            <DialogActions>
              <MDButton onClick={handleDialogClose} color="warning" variant="outlined">
                Cancel
              </MDButton>
              <MDButton color="success" variant="outlined" type="submit">
                {currentOffer.side === "BUY" ? "Sell" : "Buy"} {currentOffer.currency.digital.code}
              </MDButton>
            </DialogActions>
          </form>
        </Dialog>
      ) : (
        <br />
      )}
      <MDBox pt={3} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={12} lg={12}>
                <MDBox sx={{ marginBottom: "40px" }}>
                  <MDBox display="flex" alignItems="center" justifyContent="space-between">
                    <MDBox>
                      <MDButton
                        variant="contained"
                        size="small"
                        color="success"
                        onClick={() => {
                          navigate("/p2p/ewallets/create");
                        }}
                      >
                        <Icon>add</Icon>&nbsp; New Request
                      </MDButton>
                    </MDBox>
                    <MDBox>
                      <MDButton
                        variant="link"
                        color={darkMode ? "white" : "dark"}
                        onClick={() => {
                          navigate("/p2p/ewallets/my-requests");
                        }}
                      >
                        My Requests
                      </MDButton>
                      <MDButton
                        variant="link"
                        color={darkMode ? "white" : "dark"}
                        onClick={() => {
                          navigate("/p2p/ewallets/history");
                        }}
                      >
                        History
                      </MDButton>
                      <MDButton
                        variant="link"
                        color={darkMode ? "white" : "dark"}
                        onClick={() => {
                          navigate("/p2p/ewallets/transactions");
                        }}
                      >
                        Transactions
                      </MDButton>
                    </MDBox>
                  </MDBox>
                </MDBox>
              </Grid>
            </Grid>
            <Grid container spacing={4}>
              {offers.length <= 0 ? (
                <Grid item xs={12} md={12} lg={12}>
                  <Card>
                    <MDBox pt={3} pb={2} px={2}>
                      <MDBox
                        component="ul"
                        display="flex"
                        flexDirection="column"
                        p={0}
                        m={0}
                        sx={{ listStyle: "none" }}
                      >
                        <small>No available peer requests found.</small>
                      </MDBox>
                    </MDBox>
                  </Card>
                </Grid>
              ) : (
                <>
                  {offers.map((offer) => (
                    <Grid item xs={12} md={3} lg={3}>
                      <Card sx={{ minHeight: "25vw" }}>
                        <MDBox width="100%" display="flex" flexDirection="column">
                          <Alert
                            severity={offer.side === "SELL" ? "error" : "success"}
                            action={
                              <MDBox
                                mt={0.5}
                              >{`${offer.currency.digital.code}/${offer.currency.fiat.code}`}</MDBox>
                            }
                          >
                            <b>{offer.side}</b>
                          </Alert>

                          <MDBox
                            spacing={0}
                            bgColor={darkMode ? "transparent" : "grey-100"}
                            borderRadius="lg"
                            p={3}
                            mb={1}
                            mt={0}
                          >
                            <MDBox
                              display="flex"
                              direction="column"
                              alignItems="center"
                              justifyContent="space-evenly"
                            >
                              <MDBox
                                sx={{ minHeight: "2.5vw" }}
                                component="img"
                                src={`${ApiConfig.api_media_url}${offer.digital_wallet.logo}`}
                                alt={offer.digital_wallet.code}
                                width="20%"
                                mr={2}
                              />
                              <MDTypography variant="h6" color="text">
                                {offer.digital_wallet.title}
                              </MDTypography>
                            </MDBox>
                            <Divider />
                            <MDBox
                              mt={3}
                              lineHeight={1}
                              display="flex"
                              flexDirection="row"
                              justifyContent="space-between"
                            >
                              <MDBox>
                                <MDTypography variant="caption" color="text">
                                  Available Amount:&nbsp;&nbsp;&nbsp;
                                </MDTypography>
                              </MDBox>
                              <MDBox>
                                <MDTypography
                                  variant="caption"
                                  fontWeight="medium"
                                  textTransform="capitalize"
                                >
                                  {formatAmount(
                                    offer.liquidity / offer.currency.digital.multiplier,
                                    offer.currency.digital.symbol
                                  )}
                                </MDTypography>
                              </MDBox>
                            </MDBox>
                            <MDBox
                              lineHeight={1}
                              display="flex"
                              flexDirection="row"
                              justifyContent="space-between"
                            >
                              <MDBox>
                                <MDTypography variant="caption" color="text">
                                  Minimum:&nbsp;&nbsp;&nbsp;
                                </MDTypography>
                              </MDBox>
                              <MDBox>
                                <MDTypography variant="caption" fontWeight="medium">
                                  {offer.allow_partial_fill
                                    ? formatAmount(
                                        offer.min_transaction_amount /
                                          offer.currency.digital.multiplier,
                                        offer.currency.digital.symbol
                                      )
                                    : formatAmount(
                                        offer.liquidity / offer.currency.digital.multiplier,
                                        offer.currency.digital.symbol
                                      )}
                                </MDTypography>
                              </MDBox>
                            </MDBox>
                            <MDBox
                              mb={1}
                              lineHeight={1}
                              display="flex"
                              flexDirection="row"
                              justifyContent="space-between"
                            >
                              <MDBox>
                                <MDTypography variant="caption" color="text">
                                  Ex. Rate:&nbsp;&nbsp;&nbsp;
                                </MDTypography>
                              </MDBox>
                              <MDBox>
                                <MDTypography variant="caption" fontWeight="medium">
                                  <span>
                                    {formatAmount(
                                      offer.exchange_rate / offer.currency.digital.multiplier,
                                      offer.currency.fiat.symbol
                                    )}{" "}
                                    / {offer.currency.digital.symbol}1
                                  </span>
                                </MDTypography>
                              </MDBox>
                            </MDBox>
                            <Divider />
                            <MDBox
                              display="flex"
                              flexDirection="row"
                              justifyContent="space-between"
                            >
                              <MDBox>
                                <MDTypography variant="caption" color="text">
                                  Est. Commission:&nbsp;&nbsp;&nbsp;
                                </MDTypography>
                              </MDBox>
                              <MDBox>
                                <MDTypography variant="caption" fontWeight="medium">
                                  <span>1.0% ({offer.currency.digital.symbol}0.5)</span>
                                </MDTypography>
                              </MDBox>
                            </MDBox>
                          </MDBox>
                          <MDBox
                            p={2}
                            display="flex"
                            alignItems="center"
                            justifyContent="space-between"
                            direction="column"
                            justify="center"
                          >
                            {user.account_identifier === offer.user ? (
                              <MDButton
                                disabled
                                color="dark"
                                variant="outlined"
                                sx={{ width: "100%" }}
                              >
                                Self Trade
                              </MDButton>
                            ) : (
                              <MDButton
                                color="info"
                                variant="contained"
                                sx={{ width: "100%" }}
                                onClick={() => {
                                  setCommissionEst(0);
                                  handleOpenDialog(offer);
                                }}
                              >
                                Accept Request
                              </MDButton>
                            )}
                          </MDBox>
                        </MDBox>
                      </Card>
                    </Grid>
                  ))}
                </>
              )}
            </Grid>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default DwOffers;
